import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import AddAudioModal from '../components/VideoEditor/AddAudioModal';
import AddVideoModal from '../components/VideoEditor/AddVideoModal';
import AspectRail from '../components/VideoEditor/AspectRail';
import ClipInspector from '../components/VideoEditor/ClipInspector';
import ConfirmDialog from '../components/ConfirmDialog';
import CollapsibleSection from '../components/VideoEditor/CollapsibleSection';
import PreviewCanvas from '../components/VideoEditor/PreviewCanvas';
import ResizablePreview from '../components/VideoEditor/ResizablePreview';
import ProjectAssetsPanel from '../components/VideoEditor/ProjectAssetsPanel';
import Timeline from '../components/VideoEditor/Timeline';
import Transport from '../components/VideoEditor/Transport';
import useEditorState, { ALL_ASPECTS, MAX_AUDIO_CLIPS } from '../components/VideoEditor/editorState';
import WizardStepHeader from '../components/Wizard/WizardStepHeader';
import { markCurrent } from '../components/Wizard/wizardState';
/* Pulled in so the editor reuses the wizard's .wizard-foot / .wizard-header
 * / .wizard-progress* / .wizard-title rules when the editor is opened in
 * wizard mode. Keeps the two surfaces visually identical without
 * duplicating CSS. */
import '../pages/WizardPage.css';
import apiClient, { API_URL, getCookie } from '../services/api';
import useResource from '../services/useResource';
import useRenderProgress from '../services/useRenderProgress';
import { MaybePaginated } from '../types/api';
import { AspectRatio, ProjectAsset, VideoProject } from '../types/editor';
import { ProgressEvent } from '../types/progress';
import '../components/VideoEditor/VideoEditor.css';
import './EditorPage.css';

const AUTOSAVE_DELAY_MS = 500;

interface EditorPageProps {
  onUseInPost: (postId: number) => void;
}

/* Build the body for a project PATCH from the current editor state. */
function projectPayload(state: ReturnType<typeof useEditorState>[0]) {
  return {
    name: state.meta.name,
    aspect: state.meta.aspect,
    width: state.meta.width,
    height: state.meta.height,
    timeline: state.timeline,
  };
}

export default function EditorPage({ onUseInPost }: EditorPageProps) {
  const [state, actions] = useEditorState();
  const navigate = useNavigate();
  const { projectId: projectIdParam } = useParams<{ projectId?: string }>();
  const projectId = projectIdParam ? Number(projectIdParam) : null;
  const [searchParams] = useSearchParams();
  const inWizard = searchParams.get('wizard') === '1';

  const [sidebarRefresh, setSidebarRefresh] = useState(0);
  /* Header slot element of the Timeline section — the Timeline portals
   * its drag-to-create icons into it. */
  const [timelineToolbarHost, setTimelineToolbarHost] = useState<HTMLElement | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Project dropdown source — re-fetched whenever sidebarRefresh bumps
   * (after a NEW, DELETE, or rename). */
  const { data: projects } = useResource<VideoProject[], MaybePaginated<VideoProject>>(
    () => apiClient.get<MaybePaginated<VideoProject>>('/editor/projects/'),
    [sidebarRefresh],
    {
      initial: [],
      transform: (data) => (Array.isArray(data) ? data : data.results || []),
    },
  );

  // Per-aspect SSE subscriptions (kept as 4 separate hook calls so React
  // can keep them stable across renders).
  const progress916 = useRenderProgress(projectId, '9:16', state.renderAttempts['9:16']);
  const progress11  = useRenderProgress(projectId, '1:1',  state.renderAttempts['1:1']);
  const progress169 = useRenderProgress(projectId, '16:9', state.renderAttempts['16:9']);
  const progress45  = useRenderProgress(projectId, '4:5',  state.renderAttempts['4:5']);

  const progressByAspect = useMemo<Record<AspectRatio, ProgressEvent | null>>(() => ({
    '9:16': progress916, '1:1': progress11, '16:9': progress169, '4:5': progress45,
  }), [progress916, progress11, progress169, progress45]);

  // When any aspect's render finishes, refresh the project so the new
  // ProjectRender row (file_url, status='done') flows into state.
  useEffect(() => {
    if (!projectId) return;
    for (const a of ALL_ASPECTS) {
      const ev = progressByAspect[a];
      if (ev && (ev.phase === 'done' || ev.phase === 'error')) {
        apiClient.get<VideoProject>(`/editor/projects/${projectId}/`)
          .then((p) => actions.setRenders(p.renders || []))
          .catch(() => { /* surfaced via load effect */ });
        return;
      }
    }
  }, [progressByAspect, projectId, actions]);

  // Mirror the active aspect's live progress into the legacy renderState.
  useEffect(() => {
    const ev = progressByAspect[state.currentAspect];
    if (!ev || ev._heartbeat) return;
    if (ev.phase !== 'done' && ev.phase !== 'error') {
      actions.setRender({
        status: ev.phase, percent: ev.percent, message: ev.message || '',
      });
    }
  }, [progressByAspect, state.currentAspect, actions]);

  useEffect(() => {
    if (!projectId) return;
    setLoadError(null);
    apiClient.get<VideoProject>(`/editor/projects/${projectId}/`)
      .then((p) => actions.loadProject(p))
      .catch((err: Error) => setLoadError(err.message || 'Failed to load project'));
  }, [projectId, actions]);

  /* Auto-fit lives inside <Timeline> now — it measures the actual
   * scroller width (which the previous page-level effect couldn't see)
   * and re-fits when `autoFitKey` changes. We pass projectId so each
   * project load starts zoomed out. */

  /* Keep a live ref to `state` so the timer callback always sees the
   * latest snapshot without forcing `state` into the effect's deps
   * (which would re-fire the effect on every reducer dispatch — and
   * since markSaveError dispatches, that loops forever after a 400). */
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  useEffect(() => {
    if (!projectId || !state.saveState.dirty) return undefined;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      actions.markSaving();
      try {
        await apiClient.put(`/editor/projects/${projectId}/`, projectPayload(stateRef.current));
        actions.markSaved();
        setSidebarRefresh((n) => n + 1);
      } catch (err) {
        actions.markSaveError((err as Error).message || 'Save failed');
      }
    }, AUTOSAVE_DELAY_MS);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
    // Trigger on the things that should cause a fresh save attempt: a
    // new edit (timeline/meta) or the dirty flag flipping back on. NOT
    // `state` itself — that would loop on every dispatch including
    // markSaveError. eslint can't see through the ref pattern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, state.timeline, state.meta, state.saveState.dirty, actions]);

  /* Refresh / tab-close / navigate-away inside the autosave debounce
   * window would drop the last edit — flush it with a keepalive PUT
   * the moment the page hides. Duplicate flushes are harmless (the
   * PUT is idempotent). */
  useEffect(() => {
    if (!projectId) return undefined;
    const flush = (): void => {
      const s = stateRef.current;
      if (!s.saveState.dirty) return;
      const token = getCookie('csrftoken');
      fetch(`${API_URL}/editor/projects/${projectId}/`, {
        method: 'PUT',
        credentials: 'include',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'X-CSRFToken': token } : {}),
        },
        body: JSON.stringify(projectPayload(s)),
      }).catch(() => { /* page is going away; best effort */ });
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [projectId]);

  /* Switching projects: flush any pending autosave first so the
   * outgoing project's last edit isn't lost. */
  const switchProject = useCallback(async (newId: number) => {
    if (newId === projectId) return;
    if (state.saveState.dirty && projectId) {
      try { await apiClient.put(`/editor/projects/${projectId}/`, projectPayload(state)); }
      catch { /* if save fails the user already sees an error banner */ }
    }
    navigate(`/editor/${newId}${inWizard ? '?wizard=1' : ''}`);
  }, [navigate, projectId, state, inWizard]);

  const createNewProject = useCallback(async () => {
    try {
      const p = await apiClient.post<VideoProject>('/editor/projects/', {
        name: `Untitled — ${new Date().toLocaleString()}`,
        aspect: '9:16',
      });
      setSidebarRefresh((n) => n + 1);
      navigate(`/editor/${p.id}${inWizard ? '?wizard=1' : ''}`);
    } catch (err) {
      window.alert((err as Error).message || 'Failed to create project');
    }
  }, [navigate, inWizard]);

  const handleAssetUploaded = useCallback((asset: ProjectAsset) => {
    actions.addAsset(asset);
  }, [actions]);

  const handleDeleteAsset = useCallback(async (assetId: number) => {
    try {
      await apiClient.delete(`/editor/projects/${projectId}/assets/${assetId}/`);
      actions.deleteAsset(assetId);
    } catch (err) {
      window.alert((err as Error).message || 'Delete failed');
    }
  }, [projectId, actions]);

  const handleRender = useCallback(async (aspects: AspectRatio[]) => {
    if (!projectId || aspects.length === 0) return;
    try {
      await apiClient.post(`/editor/projects/${projectId}/render/`, { aspects });
      for (const a of aspects) actions.bumpRenderAttempt(a);
    } catch (err) {
      window.alert((err as Error).message || 'Failed to start render');
    }
  }, [projectId, actions]);

  const handleDeleteRender = useCallback(async (aspect: AspectRatio) => {
    if (!projectId) return;
    if (!window.confirm(`Delete the ${aspect} render?`)) return;
    try {
      await apiClient.delete(`/editor/projects/${projectId}/renders/${aspect}/`);
      // Optimistic local drop; the next render or load will reconcile.
      actions.setRenders(state.renders.filter((r) => r.aspect !== aspect));
    } catch (err) {
      window.alert((err as Error).message || 'Delete failed');
    }
  }, [projectId, actions, state.renders]);

  /* Give every image clip the chosen duration, then re-pack the whole
   * video track sequentially (original order, no gaps/overlaps) so the
   * slideshow stays continuous after the resize. */
  const applyDurationToImages = useCallback((duration: number) => {
    const dur = Math.max(0.2, duration);
    const ordered = [...state.timeline.video.clips].sort((a, b) => a.start - b.start);
    let cursor = 0;
    for (const c of ordered) {
      const a = state.assets.find((x) => x.id === c.assetId);
      const isImage = a?.kind === 'image';
      const trimOut = isImage ? c.trimIn + dur : c.trimOut;
      actions.updateClip('video', c.id, { start: cursor, ...(isImage ? { trimOut } : {}) });
      cursor += trimOut - c.trimIn;
    }
  }, [state.timeline.video.clips, state.assets, actions]);

  const handleContinue = useCallback(() => {
    if (!projectId) return;
    navigate(`/start/${projectId}/accounts`);
  }, [projectId, navigate]);

  const handleWizardBack = useCallback(() => {
    if (!projectId) return;
    navigate(`/start/${projectId}/upload`);
  }, [projectId, navigate]);

  /* Combo-box picker: clicking the down-arrow inside the name input
   * opens a list of every project so the user can switch without
   * leaving the editor. Local UI state; outside-click + Escape close
   * it. */
  const [pickerOpen, setPickerOpen] = useState(false);

  /* Project-list deletion (the no-project landing list below). */
  const [pendingDeleteProject, setPendingDeleteProject] = useState<VideoProject | null>(null);
  const deleteProject = useCallback(async (p: VideoProject) => {
    setPendingDeleteProject(null);
    try {
      await apiClient.delete(`/editor/projects/${p.id}/`);
      setSidebarRefresh((n) => n + 1);
    } catch (err) {
      window.alert((err as Error).message || 'Delete failed');
    }
  }, []);

  /* Add-clip modal state. Holds the timeline position the user
   * clicked, separate per track because the two modals filter
   * different asset kinds. null = closed. On pick / upload the
   * resulting clip drops at the recorded time. */
  const [addAudioAt, setAddAudioAt] = useState<number | null>(null);
  const [addVideoAt, setAddVideoAt] = useState<number | null>(null);
  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onClick = (): void => setPickerOpen(false);
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setPickerOpen(false);
    };
    window.addEventListener('click', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);

  return (
    <div className="editor-page">
      {/* Wizard step header (only when entered via ?wizard=1) so
          step 2 (Edit) shows the same title + counter + dots as
          every other wizard step. Lives above the project picker
          so the wizard chrome reads first. */}
      {projectId && inWizard && <WizardStepHeader step="edit" title="Edit your video" />}

      {/* Combo row: editable name input (with dropdown trigger tucked
          inside on the right) + "+ New" button alongside. Always
          rendered — even before a project is picked — so the user
          can switch or create one without a separate empty-state UI. */}
      <div className="editor-project-row">
        <div
          className="editor-project-combo"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            className="editor-project-name-input"
            value={projectId ? state.meta.name : ''}
            onChange={(e) => actions.setMeta({ name: e.target.value })}
            placeholder={projectId ? 'Project name' : 'No project selected — pick one or hit + New'}
            disabled={!projectId}
          />
          <button
            type="button"
            className="editor-project-combo-arrow"
            onClick={() => setPickerOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={pickerOpen}
            aria-label="Switch project"
            title="Switch project"
          >▾</button>
          {pickerOpen && (
            <ul className="editor-project-picker" role="listbox">
              {projects.length === 0 && (
                <li className="editor-project-picker-empty muted">No projects yet.</li>
              )}
              {projects.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className={p.id === projectId ? 'is-current' : ''}
                    role="option"
                    aria-selected={p.id === projectId}
                    onClick={() => { setPickerOpen(false); switchProject(p.id); }}
                  >
                    {p.name || `Project #${p.id}`}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button type="button" className="primary btn--sm" onClick={createNewProject}>
          + New
        </button>
      </div>

      {projectId && loadError && <div className="banner banner--error">{loadError}</div>}

      {!projectId && (
        projects.length === 0 ? (
          <div className="editor-empty-state muted">
            No projects yet — hit + New to start one.
          </div>
        ) : (
          <ul className="editor-project-list panel">
            {projects.map((p) => {
              const doneAspects = (p.renders || [])
                .filter((r) => r.status === 'done' && r.file_url)
                .map((r) => r.aspect);
              return (
                <li key={p.id} className="editor-project-list-row">
                  <button
                    type="button"
                    className="editor-project-list-open"
                    onClick={() => {
                      /* Opening from the list joins the wizard flow at
                         its Edit step — Back/Next then carry on into
                         accounts → compose → publish for this project. */
                      markCurrent(p.id);
                      navigate(`/editor/${p.id}?wizard=1`);
                    }}
                  >
                    <span className="editor-project-list-name">
                      {p.name || `Project #${p.id}`}
                    </span>
                    <span className="muted editor-project-list-meta">
                      {new Date(p.updated_at).toLocaleDateString()}
                      {doneAspects.length > 0 && ` · rendered ${doneAspects.join(', ')}`}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="danger btn--icon"
                    onClick={() => setPendingDeleteProject(p)}
                    aria-label={`Delete ${p.name || `project #${p.id}`}`}
                    title="Delete project"
                  >
                    🗑
                  </button>
                </li>
              );
            })}
          </ul>
        )
      )}

      {pendingDeleteProject && (
        <ConfirmDialog
          title={`Delete "${pendingDeleteProject.name || `Project #${pendingDeleteProject.id}`}"?`}
          message={(
            <p className="muted">
              Deletes the project, its uploaded assets, and its renders.
              Posts that already attached a render keep their copy.
            </p>
          )}
          onConfirm={() => deleteProject(pendingDeleteProject)}
          onCancel={() => setPendingDeleteProject(null)}
        />
      )}

      {projectId && !loadError && (
        <div className="editor-stack">
          <div className="editor-save-status muted">
            {state.saveState.saving ? 'Saving…'
              : state.saveState.error ? `Save failed: ${state.saveState.error}`
              : state.saveState.lastSavedAt ? 'Saved'
              : ''}
          </div>

          <ResizablePreview>
            <PreviewCanvas
              timeline={state.timeline}
              aspect={state.currentAspect}
              assets={state.assets}
              playing={state.playing}
              playhead={state.playhead}
              onPlayheadUpdate={actions.seek}
              onPlayingChange={actions.setPlaying}
              onSelectClip={actions.selectClip}
            />
          </ResizablePreview>

          <Transport
            playing={state.playing}
            playhead={state.playhead}
            duration={state.timeline.project.duration}
            clipStarts={[
              ...state.timeline.video.clips.map((c) => c.start),
              ...state.timeline.audio.clips.map((c) => c.start),
            ]}
            onTogglePlay={() => actions.setPlaying(!state.playing)}
            onSeek={actions.seek}
            onSetPlaying={actions.setPlaying}
          />

          <CollapsibleSection
            title="Timeline"
            badge={`${state.timeline.video.clips.length + state.timeline.audio.clips.length} clips`}
            defaultOpen
            flush
            onActionsHost={setTimelineToolbarHost}
          >
            <Timeline
              timeline={state.timeline}
              assets={state.assets}
              pxPerSec={state.pxPerSec}
              playhead={state.playhead}
              selectedClip={state.selectedClip}
              autoFitKey={projectId ?? undefined}
              toolbarHost={timelineToolbarHost}
              onMoveClip={actions.moveClip}
              onTrimClip={actions.trimClip}
              onSelectClip={actions.selectClip}
              onDeleteClip={actions.deleteClip}
              onSplitClip={actions.splitClip}
              onSetFraming={(clipId, framing) => actions.updateFraming(state.currentAspect, clipId, framing)}
              onSeek={actions.seek}
              onSetPxPerSec={actions.setPxPerSec}
              onAddAudioAt={(t) => setAddAudioAt(t)}
              onAddVideoAt={(t) => setAddVideoAt(t)}
              onAddTextAt={actions.addTextAt}
              onDropAsset={(time, assetId) => {
                const asset = state.assets.find((a) => a.id === assetId);
                if (!asset) return;
                const track = asset.kind === 'audio' ? 'audio' : 'video';
                if (track === 'audio' && state.timeline.audio.clips.length >= MAX_AUDIO_CLIPS) return;
                actions.addClipFromAsset(asset, time);
              }}
            />
          </CollapsibleSection>

          {/* Selected-clip inspector (framing, trim, transitions). Sits
              under the timeline so the user has the preview / timeline
              + the controls visible together. */}
          <ClipInspector
            selection={state.selectedClip}
            timeline={state.timeline}
            assets={state.assets}
            currentAspect={state.currentAspect}
            onTrim={(patch) => state.selectedClip && actions.trimClip(state.selectedClip.track, state.selectedClip.id, patch)}
            onUpdate={(patch) => state.selectedClip && actions.updateClip(state.selectedClip.track, state.selectedClip.id, patch)}
            onDelete={() => state.selectedClip && actions.deleteClip(state.selectedClip.track, state.selectedClip.id)}
            onClose={() => actions.selectClip(null)}
            onUpdateFraming={(clipId, patch) => actions.updateFraming(state.currentAspect, clipId, patch)}
            onResetFraming={(clipId) => actions.resetFraming(state.currentAspect, clipId)}
            onApplyPatchToSimilar={(patch, kind) => {
              /* Copy the patch to every other video-track clip whose
               * source asset matches the active clip's kind. Skip the
               * source clip itself (the user already edited that one). */
              const sourceId = state.selectedClip?.id;
              for (const c of state.timeline.video.clips) {
                if (c.id === sourceId) continue;
                const a = state.assets.find((x) => x.id === c.assetId);
                if (a?.kind !== kind) continue;
                actions.updateClip('video', c.id, patch);
              }
            }}
            onApplyDurationToImages={applyDurationToImages}
            onApplyFramingToSimilar={(framing, kind) => {
              const sourceId = state.selectedClip?.id;
              for (const c of state.timeline.video.clips) {
                if (c.id === sourceId) continue;
                const a = state.assets.find((x) => x.id === c.assetId);
                if (a?.kind !== kind) continue;
                actions.updateFraming(state.currentAspect, c.id, framing);
              }
            }}
          />

          <ProjectAssetsPanel
            projectId={projectId}
            assets={state.assets}
            onUploaded={handleAssetUploaded}
            onReorder={actions.reorderAssets}
            onDelete={handleDeleteAsset}
            onAddClip={actions.addClipFromAsset}
          />

          <CollapsibleSection
            title="Aspect Ratios"
            badge={state.currentAspect}
            defaultOpen
            flush
          >
            <AspectRail
              projectId={projectId}
              currentAspect={state.currentAspect}
              renders={state.renders}
              progressByAspect={progressByAspect}
              hasClips={state.timeline.video.clips.length > 0}
              onSelectAspect={actions.setCurrentAspect}
              onRender={handleRender}
              onDeleteRender={handleDeleteRender}
            />
          </CollapsibleSection>

          {/* Wizard footer (only when ?wizard=1). Reuses the wizard
              chrome's .wizard-foot / .wizard-foot-inner classes (CSS
              from WizardPage.css is imported above) so size, padding,
              and layout are byte-for-byte identical to every other
              step's footer. */}
          {inWizard && (
            <div className="wizard-foot">
              <div className="wizard-foot-inner">
                <button
                  type="button"
                  className="ghost btn--sm"
                  onClick={handleWizardBack}
                >
                  ← Back
                </button>
                <button
                  type="button"
                  className="primary btn--sm"
                  onClick={handleContinue}
                >
                  Next →
                </button>
              </div>
            </div>
          )}

          {/* Standalone-editor handoff (preserved for parity with the
              old Use-in-post flow). Hidden in wizard mode — the sticky
              Continue replaces it. */}
          {!inWizard && state.renders.some((r) => r.status === 'done') && (
            <div className="editor-handoff">
              <button
                type="button"
                className="secondary"
                onClick={async () => {
                  try {
                    const result = await apiClient.post<{ post_id: number }>(
                      `/editor/projects/${projectId}/use_in_post/`, {},
                    );
                    onUseInPost(result.post_id);
                  } catch (err) {
                    window.alert((err as Error).message || 'Failed to attach to post');
                  }
                }}
              >
                Use in a post
              </button>
            </div>
          )}
        </div>
      )}

      {/* Audio picker — opens when the user taps an empty spot on the
          audio track and no audio clip exists yet. Pick an existing
          asset or upload one; either way the audio drops at the
          recorded time. */}
      {projectId && addAudioAt !== null && (
        <AddAudioModal
          projectId={projectId}
          assets={state.assets}
          atTime={addAudioAt}
          onPick={(asset) => {
            actions.addClipFromAsset(asset, addAudioAt);
            setAddAudioAt(null);
          }}
          onUploaded={(asset) => {
            actions.addAsset(asset);
            actions.addClipFromAsset(asset, addAudioAt);
            setAddAudioAt(null);
          }}
          onClose={() => setAddAudioAt(null)}
        />
      )}

      {/* Video-track picker — opens on empty-area tap on the video
          track. Accepts image OR video assets; drops the chosen clip
          at the recorded time. No cap on the video track. */}
      {projectId && addVideoAt !== null && (
        <AddVideoModal
          projectId={projectId}
          assets={state.assets}
          atTime={addVideoAt}
          onPick={(asset) => {
            actions.addClipFromAsset(asset, addVideoAt);
            setAddVideoAt(null);
          }}
          onUploaded={(asset) => {
            actions.addAsset(asset);
            actions.addClipFromAsset(asset, addVideoAt);
            setAddVideoAt(null);
          }}
          onClose={() => setAddVideoAt(null)}
        />
      )}
    </div>
  );
}
